import type { PropsWithChildren } from 'react';
import type React from 'react';

import { cn } from '@change/ds-utils';

import { HighlightText } from '../HighlightText';

import { useSelectionCard } from './hooks/useSelectionCard';
import { RadioLabel } from './RadioLabel';

export type Props = PropsWithChildren<{
	highlight?: string;
	label: string;
	message: string;
	onSelect: () => void;
	checked: boolean;
	qaSelectorSelection: string;
	qaSelectorLabel: string;
	qaSelectorMessage: string;
}>;

export const Selection: React.FC<Props> = ({
	qaSelectorSelection,
	qaSelectorLabel,
	qaSelectorMessage,
	label,
	message,
	highlight,
	onSelect,
	checked,
	children,
}) => {
	// `handleKeyDown` is provided by the hook because the outer container uses
	// role="radio" on a <div>, which has no native keyboard activation — the
	// hook centralizes the Space/Enter handling that triggers `onSelect`.
	const {
		data: { borderBackgroundClass, backgroundClass },
		actions: { handleKeyDown },
	} = useSelectionCard(checked, onSelect);

	return (
		<div
			role="radio"
			aria-checked={checked}
			aria-label={label}
			tabIndex={0}
			data-qa={qaSelectorSelection}
			data-testid="selection-radio"
			className={cn(
				'rounded-large text-typography-strong cursor-pointer',
				checked
					? 'elevation-elevated-selected m-0.5 p-0.5'
					: 'border-stroke-weaker elevation-low border-2 border-solid',
				borderBackgroundClass,
			)}
			onClick={onSelect}
			onKeyDown={handleKeyDown}
		>
			{highlight && <HighlightText text={highlight} selected={checked} />}
			<div
				className={cn(
					'px-8 pb-4',
					highlight ? 'rounded-t-none rounded-b-[11px] pt-3' : 'rounded-[11px] pt-4',
					backgroundClass,
				)}
			>
				<RadioLabel
					qaSelectorLabel={qaSelectorLabel}
					qaSelectorMessage={qaSelectorMessage}
					label={label}
					message={message}
				>
					{children}
				</RadioLabel>
			</div>
		</div>
	);
};
